import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, Box, Button, Divider, Drawer, TextField, IconButton, Grid, Paper, InputAdornment, useMediaQuery, CircularProgress, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Autocomplete from '@mui/material/Autocomplete';
import { clonePublicStrategy,  cacheAssetDetails, updateStrategy, fetchStrategyById } from '../../features/strategySlice';
import { closeStrategyDrawer, clearDrawer } from '../../features/drawerSlice';
import { axiosInstance } from '../../axiosConfig';
import CircularProgressWithLabel from './CircularProgressWithLabel';
import { useNavigate } from 'react-router-dom';
import theme from '../../theme';

const useStyles = makeStyles((theme) => ({
  drawerContent: {
    width: '700px',
    height: '100%',
    maxWidth: 900,
    padding: theme.spacing(3),
    backgroundColor: '#F6F8FB',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      padding: theme.spacing(2),
    },
  },
  header: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'space-between',
      marginBottom: theme.spacing(2),
    },
  },
  formControl: {
    marginBottom: theme.spacing(2),
  },
  addButton: {
    marginTop: theme.spacing(2),
    backgroundColor: '#00D0AF',
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#00B89C',
    },
  },
  actionButtons: {
    marginTop: theme.spacing(4),
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
  },
  button: {
    display: 'flex',
    width: '100%',
    borderRadius: '0px!important',
    padding: '12px 16px!important',
    textTransform: "capitalize",
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    justifyContent: 'space-between',
    flexDirection: 'row',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  percentageCircle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 55,
    height: 55,
  },
  removeButton: {
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(2),
  },
}));


const InfoDrawer = ({ open, onClose }) => {
    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: '500px' },
            padding: '24px',
            backgroundColor: '#F6F8FB',
          },
        }}
      >
        <Grid container p={2}>
          <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Information UK funds
              </Typography>
              <IconButton onClick={onClose} sx={{ padding: '12px', borderRadius: '50%', background: '#EFF0F6' }}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Paper elevation={2} sx={{ padding: '24px', marginBottom: '15px' }}>
              <Typography variant="h6" sx={{ marginBottom: '16px', color: '#1D2155' }}>
              The first tab on the spreadsheet lists the predefined funds in the app on the left hand side and then identifies the single closest matching UK fund to the right of it.<p/> This is useful if you want to recreate one of the predefined famous strategies with funds available in the UK.
<p />The second tab allows you to match a list of UK funds to the closest matching predefined fund in the app. <p />This is useful if you want to choose funds to recreate your own strategy in the app based on a UK fund portfolio.
<p />The spreadsheet with the mapping is <a href="https://docs.google.com/spreadsheets/d/1dzNh3ep2928KqWJ4IKyODXeL3Orf-u4em2YLmN58yug/edit?gid=0#gid=0" target="_blank" rel="noopener noreferrer">here</a>
              </Typography>


            </Paper>


          </Box>
        </Grid>
      </Drawer>
    );
  };

const CreateStrategyDrawer = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const open = useSelector((state) => state.drawer.open);
  const currentStrategy = useSelector((state) => state.drawer.strategyData);
  const [strategyName, setStrategyName] = useState('');
  const [funds, setFunds] = useState([{ fund: null, allocation: '' }]);
  const [errorMessage, setErrorMessage] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [infoDrawerOpen, setInfoDrawerOpen] = useState(false);

  // Currency symbols mapping
  const currencySymbols = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£'
  };

  // Helper function to get currency symbol
  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  // Per-row state for search functionality to avoid conflicts between Autocomplete instances
  const [rowStates, setRowStates] = useState([]);
  const searchTimers = useRef({});

  const publicStrategies = useSelector(
    (state) => state.strategy.publicStrategies
  );
  const assetDetailsCache = useSelector((state) => state.strategy.assetDetailsCache);

  // Helper to update row state
  const setRowState = (index, updates) => {
    setRowStates(prev => {
      const newStates = [...prev];
      newStates[index] = { ...newStates[index], ...updates };
      return newStates;
    });
  };

  // Function to search assets via API with debouncing per row
  const searchAssets = async (index, searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setRowState(index, { results: [], loading: false });
      return;
    }

    setRowState(index, { loading: true });
    try {
      const response = await axiosInstance.get('/search', {
        params: {
          search_term: searchTerm
        }
      });

      // Transform API response to match expected format - using correct field names from API
      const apiData = Array.isArray(response.data) ? response.data : [];

      const transformedResults = apiData.map(asset => {
        // Ensure we have the proper ticker.exchange format
        let fullTicker = asset.Code;
        if (asset.Exchange && !asset.Code.includes('.')) {
          fullTicker = `${asset.Code}.${asset.Exchange}`;
        } else if (!asset.Exchange && !asset.Code.includes('.')) {
          // Default to US exchange if no exchange specified
          fullTicker = `${asset.Code}.US`;
        }

        return {
          label: asset.Code,
          description: asset.Name,
          ticker: fullTicker,
          originalCode: asset.Code,
          exchange: asset.Exchange || 'US',
          type: asset.Type,
          country: asset.Country,
          currency: asset.Currency,
          isin: asset.ISIN
        };
      });

      const uniqueResults = [...new Map(transformedResults.map(item => [item.ticker, item])).values()];

      setRowState(index, { results: uniqueResults, loading: false });
    } catch (error) {
      console.error('Error searching assets:', error);
      setRowState(index, { results: [], loading: false });
    }
  };

  // Debounced search function per row with better cleanup
  const debouncedSearch = (index, searchTerm) => {
    // Clear any existing timer for this row
    if (searchTimers.current[index]) {
      clearTimeout(searchTimers.current[index]);
    }

    // If search term is too short, clear results immediately
    if (!searchTerm || searchTerm.length < 2) {
      setRowState(index, { results: [], loading: false });
      return;
    }

    // Set new timer for this specific row
    searchTimers.current[index] = setTimeout(() => {
      searchAssets(index, searchTerm);
    }, 300);
  };

  // Cleanup all search timers on unmount
  useEffect(() => {
    return () => {
      Object.values(searchTimers.current).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
    };
  }, []);

    useEffect(() => {
    if (open && currentStrategy) {
      setStrategyName(currentStrategy.name || '');
      if (currentStrategy.assets && currentStrategy.assets.length > 0) {


        // First pass: convert all to percentages with proper rounding (max 1 decimal)
        const percentages = currentStrategy.assets.map(asset => {
          const p = asset.weight * 100;
          return p % 1 === 0 ? parseFloat(p.toFixed(0)) : parseFloat(p.toFixed(1));
        });

        // Calculate the sum
        const sum = percentages.reduce((acc, val) => acc + val, 0);

        // Adjust the last one if needed to make exactly 100
        if (Math.abs(sum - 100) > 0.001) {
          percentages[percentages.length - 1] = parseFloat((100 - percentages.slice(0, -1).reduce((acc, val) => acc + val, 0)).toFixed(1));
        }

        const newFunds = currentStrategy.assets.map((asset, index) => {
          const ticker = asset.ticker || asset.asset_ticker;
          const originalCode = ticker.includes('.') ? ticker.substring(0, ticker.lastIndexOf('.')) : ticker;
          const cachedAsset = assetDetailsCache[ticker];
          const description = asset.description || asset.asset_description || cachedAsset?.description || ticker;

          return {
            fund: {
              ticker: ticker,
              description: description,
              originalCode: originalCode,
            },
            allocation: percentages[index] % 1 === 0 ? percentages[index].toFixed(0) : percentages[index].toFixed(1)
          }
        });

        setFunds(newFunds);
        // Initialize row states for existing funds
        setRowStates(newFunds.map(() => ({ input: "", results: [], loading: false })));
      } else {
        setFunds([{ fund: null, allocation: '' }]);
        setRowStates([{ input: "", results: [], loading: false }]);
      }
    } else {
      resetForm();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentStrategy]);



  const resetForm = () => {
    setStrategyName('');
    setFunds([{ fund: null, allocation: '' }]);
    setErrorMessage('');
    setRowStates([{ input: "", results: [], loading: false }]);
    setIsCreating(false);
    // Clear all search timers
    Object.values(searchTimers.current).forEach(timer => {
      if (timer) clearTimeout(timer);
    });
    searchTimers.current = {};
  };


  const handleAddFund = () => {
    setFunds([...funds, { fund: null, allocation: '' }]);
    setRowStates([...rowStates, { input: "", results: [], loading: false }]);
  };

  const handleRemoveFund = (index) => {
    const newFunds = funds.filter((_, i) => i !== index);
    setFunds(newFunds);
    const newRowStates = rowStates.filter((_, i) => i !== index);
    setRowStates(newRowStates);
    // Clear timer for this row
    if (searchTimers.current[index]) {
      clearTimeout(searchTimers.current[index]);
      delete searchTimers.current[index];
    }
  };

  const handleFundChange = (index, field, value) => {
    const newFunds = [...funds];
    if (field === 'allocation') {
      // Clean the input but keep the percentage format
      let cleanValue = value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1');

      if (cleanValue === '') {
        newFunds[index][field] = '';
      } else {
        // Limit to 1 decimal place
        const parts = cleanValue.split('.');
        if (parts[1] && parts[1].length > 1) {
          cleanValue = `${parts[0]}.${parts[1].charAt(0)}`;
        }

        // Validate max 100%
        const numValue = parseFloat(cleanValue);
        if (!isNaN(numValue) && numValue > 100) {
          newFunds[index][field] = '100';
        } else {
          newFunds[index][field] = cleanValue;
        }
      }
    } else {
      newFunds[index][field] = value;
      if (field === 'fund' && value) {
        dispatch(cacheAssetDetails(value));
        // Clear search results when a fund is selected and update input
        setRowState(index, {
          results: [],
          input: value.description || value.ticker || value.originalCode || ''
        });
      }
    }
    setFunds(newFunds);
  };

  const hasDuplicateAssets = () => {
    const assetTickers = funds
      .map(fund => fund.fund?.ticker?.trim()?.toUpperCase())
      .filter(Boolean);
    return assetTickers.length !== new Set(assetTickers).size;
  };

  const handleSaveStrategy = async () => {
    // Convert percentage to decimal for validation (divide by 100)
    const totalAllocation = Number(
      funds.reduce((total, item) => total + Number(item.allocation || 0) / 100, 0).toFixed(4)
    );
    const isTotalOk = Math.abs(totalAllocation - 1) <= 0.001;

    if (!strategyName || strategyName.trim() === '') {
        setErrorMessage('Please provide a name for your strategy.');
        return;
      }

    if (hasDuplicateAssets()) {
      setErrorMessage('You cannot add the same asset more than once.');
      return;
    }

    if (!isTotalOk) {
      setErrorMessage(`Total allocation must be 100%. Current: ${(totalAllocation * 100).toFixed(2)}%`);
      return;
    }

    if (strategyName && isTotalOk) {
      setIsCreating(true);
      const assetsForPayload = funds
        .map((item) => ({
          ticker: item.fund?.ticker,
          weight: parseFloat((parseFloat(item.allocation) / 100).toFixed(3)),
          description: item.fund?.description,
        }))
        .filter(item => item.ticker);


      // Calculate sum of all weights except the last one
      if (assetsForPayload.length > 0) {
        const sumOfOthers = assetsForPayload.slice(0, -1).reduce((sum, asset) => sum + asset.weight, 0);
        const lastIndex = assetsForPayload.length - 1;

        // Adjust the last weight to make total exactly 1.000
        assetsForPayload[lastIndex].weight = parseFloat((1 - sumOfOthers).toFixed(3));

      }

      // Final validation
      let apiPayload = {
        name: strategyName,
        assets: assetsForPayload,
      };


      const buildFatAssets = () => assetsForPayload.map(asset => ({
        ticker: asset.ticker,
        asset_ticker: asset.ticker,
        weight: asset.weight,
        asset_description: asset.description,
        description: asset.description,
        asset_type: funds.find(f => f.fund?.ticker === asset.ticker)?.fund?.type,
      }));

      try {
        if (currentStrategy?.id) {
          if (publicStrategies.some((publicStrat) => publicStrat.id === currentStrategy.id)) {
            const response = await dispatch(clonePublicStrategy(apiPayload)).unwrap();
            if (response?.id) {

              await dispatch(fetchStrategyById({ id: response.id })).unwrap();

              dispatch(closeStrategyDrawer());
              resetForm();
              navigate(`/strategy/${response.id}`);
            } else {
              console.error('Failed to clone public strategy');
            }
          } else {
            const response = await axiosInstance.put(`/strategies/${currentStrategy.id}/`, { ...apiPayload, id: currentStrategy.id });

            try {
              const completeStrategyData = await dispatch(fetchStrategyById({ id: currentStrategy.id })).unwrap();

              const updatedStrategy = {
                ...completeStrategyData,
                assets: buildFatAssets(),
                name: strategyName
              };

              dispatch(updateStrategy(updatedStrategy));
              dispatch(closeStrategyDrawer());
              resetForm();
            } catch (fetchError) {
              console.warn('Failed to fetch updated strategy data, using PUT response:', fetchError);
              const updatedStrategy = {
                ...response.data,
                assets: buildFatAssets(),
                name: strategyName
              };
              dispatch(updateStrategy(updatedStrategy));
              dispatch(closeStrategyDrawer());
              resetForm();
            }
          }
        } else {
          setIsCreating(true);

          const response = await axiosInstance.post('/strategies/', apiPayload);

          await dispatch(fetchStrategyById({ id: response.data.id })).unwrap();

          dispatch(closeStrategyDrawer());
          resetForm();
          navigate(`/strategy/${response.data.id}`);
        }
      } catch (error) {
        console.error('❌ Error saving strategy:', error);
        console.error('📋 Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        setErrorMessage('Failed to save strategy. Please try again.');
      } finally {
        setIsCreating(false);
      }
    }
  };


  // Convert percentage to decimal for validation (divide by 100)
  const totalAllocation = Number(funds.reduce((total, item) => total + Number(item.allocation || 0) / 100, 0).toFixed(4));
  const isTotalOk = Math.abs(totalAllocation - 1) <= 0.002; // Tolerance: 0.2% (max rounding error with 1 decimal)
  const nameOk = strategyName && strategyName.trim() !== '';
  const noDuplicates = !hasDuplicateAssets();
  const canSave = nameOk && noDuplicates && isTotalOk && !isCreating;

  // Debug logging
  useEffect(() => {
    if (funds.some(f => f.allocation)) {
      console.log('🔍 Validation Debug:', {
        allocations: funds.map(f => f.allocation),
        totalAllocation: totalAllocation,
        totalPercentage: (totalAllocation * 100).toFixed(2) + '%',
        difference: Math.abs(totalAllocation - 1),
        isTotalOk: isTotalOk,
        canSave: canSave
      });
    }
  }, [funds, totalAllocation, isTotalOk, canSave]);

  useEffect(() => {
    if (!nameOk && strategyName.length > 0) {
      setErrorMessage('Please provide a valid strategy name.');
    } else if (!noDuplicates) {
      setErrorMessage('You cannot add the same asset more than once.');
    } else if (totalAllocation > 1.002) {
      setErrorMessage(`Distribute ${((totalAllocation - 1) * 100).toFixed(1)}% less to save your strategy`);
    } else if (totalAllocation < 0.998 && funds.some(f => f.allocation)) {
      setErrorMessage(`Add ${((1 - totalAllocation) * 100).toFixed(1)}% more to reach 100%`);
    } else {
      setErrorMessage('');
    }
  }, [totalAllocation, nameOk, noDuplicates, strategyName, funds]);

  const onHandleClose = () => {
    // Clear any pending searches
    Object.values(searchTimers.current).forEach(timer => {
      if (timer) clearTimeout(timer);
    });

    dispatch(clearDrawer());
    dispatch(closeStrategyDrawer());
    resetForm();
  }

  return (
    <Drawer anchor="right" open={open} onClose={onHandleClose}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', width: { xs: '100%', sm: '700px' }, backgroundColor: '#F6F8FB' }}>

        {/* Non-scrollable Header */}
        <Box sx={{ p: 3, flexShrink: 0, borderBottom: '1px solid #E5E7EB', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
          {/* Main Drawer Title & Close Button */}
          <Grid container sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Grid item>
              <Typography variant="h3" sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                {currentStrategy?.name ? 'Edit your Strategy' : 'Create your Strategy'}
              </Typography>
              <Divider sx={{ borderBottomWidth: 4, width: '80px', borderColor: '#00D0AF', opacity: 1 }} />
            </Grid>
            <Grid item>
              <IconButton onClick={onHandleClose} sx={{ padding: '12px', borderRadius: '50%', background: '#EFF0F6' }}>
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>

          {/* Strategy Name */}
          <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6">Strategy Name</Typography>
            <TextField
              fullWidth
              label="Strategy Name"
              placeholder="Please type the name of your strategy"
              variant="outlined"
              margin="normal"
              value={strategyName}
              onChange={(e) => setStrategyName(e.target.value)}
            />
          </Paper>

          {/* "Add Funds" Title and Progress */}
          <Grid container sx={{ alignItems: 'center', mt: 2 }}>
            <Grid item xs={12} sm={8}>
              <Typography textAlign={isMobile ? "center" : 'left'} variant="subtitle1">Add funds to your strategy</Typography>
            </Grid>
            <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-end' }, alignItems: 'center', mt: { xs: 1, sm: 0 } }}>
              <CircularProgressWithLabel value={totalAllocation * 100} />
            </Grid>
            {errorMessage && (
              <Grid item xs={12} mt={1}>
                <Typography variant="body2" color="error" textAlign="center">
                  {errorMessage}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Loading Spinner Overlay */}
        {isCreating && (
          <Box
            sx={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', zIndex: 1000,
            }}
          >
            <CircularProgress size={40} sx={{ color: '#008ED1', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#008ED1' }}>Creating your strategy...</Typography>
            <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>Please wait while we save your data</Typography>
          </Box>
        )}

        {/* Scrollable Content */}
        <Box sx={{ overflowY: 'auto', flexGrow: 1, p: 3 }}>
          {funds.map((item, index) => (
            <Paper key={index} elevation={2} sx={{ p: 3, mb: 2 }}>
              <Grid container alignItems="center">
                <Grid item xs={12} sm={7} lg={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="sidebar" sx={{ fontSize: '16px', color: '#1D2155' }}>Fund</Typography>
                    {item.fund?.description && (
                      <Tooltip title={item.fund.description} arrow placement="top">
                        <IconButton size="small" sx={{ p: 0.25 }}>
                          <InfoOutlinedIcon fontSize="small" sx={{ color: '#008ED1' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Tooltip title={item.fund?.description || "Type to search for funds"} arrow placement="top" disableHoverListener={!item.fund?.description}>
                    <Autocomplete
                      fullWidth
                      options={rowStates[index]?.results || []}
                      loading={rowStates[index]?.loading || false}
                      inputValue={(() => {
                        if (item.fund && item.fund.ticker && item.fund.description) {
                          const ticker = item.fund.originalCode || item.fund.ticker;
                          const description = item.fund.description;
                          if (ticker !== description) {
                            const maxDescLength = 40;
                            const truncatedDesc = description.length > maxDescLength ? description.substring(0, maxDescLength) + '...' : description;
                            return `${ticker} - ${truncatedDesc}`;
                          }
                        }
                        return rowStates[index]?.input || '';
                      })()}
                      getOptionLabel={(option) => {
                        if (!option) return '';
                        if (typeof option === 'string') return option;
                        const ticker = option.originalCode || option.ticker;
                        const description = option.description;
                        if (ticker && description && ticker !== description) {
                          const maxDescLength = 40;
                          const truncatedDesc = description.length > maxDescLength ? description.substring(0, maxDescLength) + '...' : description;
                          return `${ticker} - ${truncatedDesc}`;
                        }
                        return ticker || description || '';
                      }}
                      value={item.fund}
                      onChange={(event, newValue) => handleFundChange(index, 'fund', newValue)}
                      onInputChange={(event, newInputValue, reason) => {
                        setRowState(index, { input: newInputValue || "" });
                        if (reason === 'input') debouncedSearch(index, newInputValue);
                        else if (reason === 'clear') setRowState(index, { results: [], input: "" });
                      }}
                      filterOptions={(x) => x}
                      isOptionEqualToValue={(option, value) => (option?.isin && value?.isin && option.isin === value.isin) || option?.ticker?.toUpperCase() === value?.ticker?.toUpperCase()}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{option.originalCode} ({option.exchange})</Typography>
                            <Typography variant="body2" color="textSecondary">{option.description}</Typography>
                            <Typography variant="caption" color="textSecondary">{option.country} • {getCurrencySymbol(option.currency)}</Typography>
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Type to search funds"
                          variant="outlined"
                          sx={{ '& .MuiOutlinedInput-root': { fontSize: '15px' } }}
                          margin="normal"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start"><SearchIcon sx={{ color: '#008ED1' }} /></InputAdornment>
                            ),
                          }}
                        />
                      )}
                      noOptionsText={rowStates[index]?.loading ? "Searching..." : (rowStates[index]?.input && rowStates[index]?.input.length >= 2 ? "No results were found" : "Type at least 2 characters to search for assets")}
                    />
                  </Tooltip>
                </Grid>
                <Grid item xs={10} sm={3} ml={{ xs: 0, sm: 2 }}>
                  <Typography variant="sidebar" sx={{ fontSize: '16px', color: '#1D2155' }}>Allocation</Typography>
                  <TextField
                    fullWidth
                    type="text"
                    variant="outlined"
                    margin="normal"
                    placeholder='%'
                    value={item.allocation}
                    onChange={(e) => handleFundChange(index, 'allocation', e.target.value)}
                    inputProps={{ inputMode: 'decimal' }}
                  />
                </Grid>
                <Grid item xs={2} sm={2} mt={{ xs: 2, sm: 3 }}>
                  <IconButton className={classes.removeButton} onClick={() => handleRemoveFund(index)}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14" id="Recycle-Bin-2--Streamline-Core-Remix" height="50" width="24"><desc>Recycle Bin 2 Streamline Icon: https://streamlinehq.com</desc><g id="Free Remix/Interface Essential/recycle-bin-2--remove-delete-empty-bin-trash-garbage"><path id="Union" fill="#008ED1" fillRule="evenodd" d="M5.85095 2.22595C6.1557 1.9212 6.56902 1.75 7 1.75c0.43098 0 0.8443 0.1712 1.14905 0.47595 0.21471 0.21472 0.36314 0.48334 0.43209 0.77405H5.41886c0.06895 -0.29071 0.21737 -0.55933 0.43209 -0.77405ZM3.64589 3c0.08462 -0.75713 0.42371 -1.46758 0.96762 -2.011485C5.24645 0.355579 6.10489 0 7 0c0.89511 0 1.75355 0.355579 2.38649 0.988515C9.9304 1.53242 10.2695 2.24287 10.3541 3H13c0.4832 0 0.875 0.39175 0.875 0.875s-0.3918 0.875 -0.875 0.875h-0.875v7.625c0 0.431 -0.1712 0.8443 -0.476 1.149 -0.3047 0.3048 -0.718 0.476 -1.149 0.476h-7c-0.43098 0 -0.8443 -0.1712 -1.14905 -0.476 -0.30475 -0.3047 -0.47595 -0.718 -0.47595 -1.149V4.75H1c-0.483249 0 -0.875 -0.39175 -0.875 -0.875S0.516751 3 1 3h2.64589Zm7.22911 9.375V4.75h-7.75v7.625c0 0.0995 0.03951 0.1948 0.10983 0.2652 0.07033 0.0703 0.16571 0.1098 0.26517 0.1098h7c0.0995 0 0.1948 -0.0395 0.2652 -0.1098 0.0703 -0.0704 0.1098 -0.1657 0.1098 -0.2652ZM5.5 6.00146c0.34518 0 0.625 0.27983 0.625 0.625V10.628c0 0.3451 -0.27982 0.625 -0.625 0.625s-0.625 -0.2799 -0.625 -0.625V6.62646c0 -0.34517 0.27982 -0.625 0.625 -0.625Zm3.625 0.625c0 -0.34517 -0.27982 -0.625 -0.625 -0.625s-0.625 0.27983 -0.625 0.625V10.628c0 0.3451 0.27982 0.625 0.625 0.625s0.625 -0.2799 0.625 -0.625V6.62646Z" clipRule="evenodd" strokeWidth="1"></path></g></svg>
                  </IconButton>
                </Grid>
              </Grid>
            </Paper>
          ))}
          <Paper elevation={0} sx={{ p: 2, mt: 1, mb: 1, backgroundColor: 'transparent' }}>
            <Grid container justifyContent='space-between' alignItems='center'>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Add another fund</Typography>
              <Button onClick={handleAddFund} disabled={totalAllocation > 1.001} aria-label="Add fund">
                <AddCircleIcon sx={{ fontSize: 32, color: '#00D0AF' }} />
              </Button>
            </Grid>
          </Paper>
        </Box>

        {/* Non-scrollable Footer */}
        <Box sx={{ p: 3, flexShrink: 0, borderTop: '1px solid #E5E7EB', backgroundColor: 'white', boxShadow: '0 -2px 4px rgba(0,0,0,0.05)' }}>
          <InfoDrawer open={infoDrawerOpen} onClose={() => setInfoDrawerOpen(false)} />
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item xs={12} sm={5}>
              <Button
                variant="text"
                fullWidth
                className={classes.button}
                disabled={isCreating}
                sx={{ backgroundColor: '#EFF0F6', opacity: isCreating ? 0.5 : 1 }}
                onClick={onHandleClose}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item xs={12} sm={7}>
              <Button
                variant="contained"
                fullWidth
                className={classes.button}
                disabled={!canSave}
                sx={{
                  backgroundColor: !canSave ? '#cccccc' : '#008ED1',
                  '&:disabled': { backgroundColor: '#cccccc', color: '#666666' }
                }}
                onClick={handleSaveStrategy}
              >
                {isCreating ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={16} sx={{ color: "#666666" }} />
                    Creating...
                  </Box>
                ) : "Save Strategy"}
              </Button>
            </Grid>
          </Grid>
        </Box>

      </Box>
    </Drawer>
  );
};

export default CreateStrategyDrawer;
